/**
 * Verbes métier des fournisseurs.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { Supplier } from "./types.js";

const optional = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .or(z.literal("").transform(() => null));

export const saveSupplierInput = z.object({
  supplierId: z.string().uuid().optional(),
  confirmationId: z.string().optional(),

  name: z.string().trim().min(1, "Le nom du fournisseur est requis"),
  contactName: optional,
  phone: optional,
  email: z
    .string()
    .email("Adresse e-mail invalide")
    .nullish()
    .or(z.literal("").transform(() => null)),
  address: optional,
  siren: z
    .string()
    .trim()
    .regex(/^\d{9}$/, "Le SIREN doit faire 9 chiffres")
    .nullish()
    .or(z.literal("").transform(() => null)),
  notes: optional,
});

export type SaveSupplierInput = z.infer<typeof saveSupplierInput>;

export const listSuppliersInput = z.object({
  confirmationId: z.string().optional(),
  includeInactive: z.boolean().default(false),
});

export type ListSuppliersInput = z.infer<typeof listSuppliersInput>;

export const archiveSupplierInput = z.object({
  confirmationId: z.string().optional(),
  supplierId: z.string().uuid(),
});

export type ArchiveSupplierInput = z.infer<typeof archiveSupplierInput>;

export interface SaveSupplierCommand {
  supplierId?: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  siren: string | null;
  notes: string | null;
}

export interface SupplierRepository {
  list(ctx: ExecutionContext, includeInactive: boolean): Promise<Supplier[]>;
  save(ctx: ExecutionContext, command: SaveSupplierCommand): Promise<Supplier>;
  archive(ctx: ExecutionContext, supplierId: string): Promise<void>;
}

export function createSupplierServices(repo: SupplierRepository) {
  const listerFournisseurs = defineService<ListSuppliersInput, Supplier[]>({
    name: "lister_fournisseurs",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, input.includeInactive),
  });

  const enregistrerFournisseur = defineService<SaveSupplierInput, Supplier>({
    name: "enregistrer_fournisseur",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      repo.save(ctx, {
        ...(input.supplierId ? { supplierId: input.supplierId } : {}),
        name: input.name,
        contactName: input.contactName ?? null,
        phone: input.phone ?? null,
        email: input.email ?? null,
        address: input.address ?? null,
        siren: input.siren ?? null,
        notes: input.notes ?? null,
      }),
  });

  const archiverFournisseur = defineService<ArchiveSupplierInput, void>({
    name: "archiver_fournisseur",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => repo.archive(ctx, input.supplierId),
  });

  return { listerFournisseurs, enregistrerFournisseur, archiverFournisseur };
}

export type SupplierServices = ReturnType<typeof createSupplierServices>;
